import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import matchingService from "../services/matchingService";

interface BankTransaction {
  id: number;
  data_movimento: string;
  descricao: string;
  valor: number;
  banco?: string;
  conta?: string;
  matched?: boolean;
  partiallyMatched?: boolean;
  matchedAmount?: number;
}

interface Invoice {
  id: number;
  data_emissao: string;
  numero_fatura: string;
  nif_emitente: string;
  emitente_nome?: string;
  emitente_nome_curto?: string;
  valor: number;
  categoria: string;
  matched?: boolean;
  partiallyMatched?: boolean;
  matchedAmount?: number;
}

const AdvancedMatchingPage: React.FC = () => {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scroll infinito
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(true);

  // Drag and drop
  const [draggedItem, setDraggedItem] = useState<{
    type: "invoice" | "transaction";
    id: number;
  } | null>(null);

  // Seleção para matching
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [selectedTransactions, setSelectedTransactions] = useState<number[]>([]);

  // Matches existentes dos itens selecionados
  const [existingMatches, setExistingMatches] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Refs para scroll infinito
  const transactionsRef = useRef<HTMLDivElement>(null);
  const invoicesRef = useRef<HTMLDivElement>(null);

  // Carregar transações (TODAS, não apenas unmatched)
  const loadTransactions = useCallback(async (page: number) => {
    try {
      const response = await fetch(
        `/api/bank-statements/transactions/all?page=${page}&limit=100`
      );
      const data = await response.json();

      if (data.success) {
        const newTransactions = (data.transactions || data.data || []).map((t: any) => ({
          ...t,
          valor: parseFloat(t.valor),
        }));
        setTransactions((prev) =>
          page === 1 ? newTransactions : [...prev, ...newTransactions]
        );
        setHasMoreTransactions(newTransactions.length === 100);
      }
    } catch (err) {
      console.error("Erro ao carregar transações:", err);
    }
  }, []);

  // Carregar faturas (TODAS, não apenas unmatched)
  const loadInvoices = useCallback(async (page: number) => {
    try {
      const response = await fetch(
        `/api/invoices/all?page=${page}&limit=100`
      );
      const data = await response.json();

      if (data.success) {
        const newInvoices = (data.invoices || data.data || []).map((i: any) => ({
          ...i,
          valor: parseFloat(i.valor),
        }));
        setInvoices((prev) =>
          page === 1 ? newInvoices : [...prev, ...newInvoices]
        );
        setHasMoreInvoices(newInvoices.length === 100);
      }
    } catch (err) {
      console.error("Erro ao carregar faturas:", err);
    }
  }, []);

  // Scroll infinito - transações
  const handleTransactionsScroll = useCallback(() => {
    if (!transactionsRef.current || !hasMoreTransactions) return;

    const { scrollTop, scrollHeight, clientHeight } = transactionsRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setTransactionsPage((prev) => prev + 1);
    }
  }, [hasMoreTransactions]);

  // Scroll infinito - faturas
  const handleInvoicesScroll = useCallback(() => {
    if (!invoicesRef.current || !hasMoreInvoices) return;

    const { scrollTop, scrollHeight, clientHeight } = invoicesRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setInvoicesPage((prev) => prev + 1);
    }
  }, [hasMoreInvoices]);

  // Carregar dados iniciais
  useEffect(() => {
    loadTransactions(1);
    loadInvoices(1);
  }, [loadTransactions, loadInvoices]);

  // Carregar mais ao fazer scroll
  useEffect(() => {
    if (transactionsPage > 1) {
      loadTransactions(transactionsPage);
    }
  }, [transactionsPage, loadTransactions]);

  useEffect(() => {
    if (invoicesPage > 1) {
      loadInvoices(invoicesPage);
    }
  }, [invoicesPage, loadInvoices]);

  // Drag handlers
  const handleDragStart = (type: "invoice" | "transaction", id: number) => {
    setDraggedItem({ type, id });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDropOnInvoice = (targetInvoiceId: number) => {
    if (!draggedItem) return;

    if (draggedItem.type === "transaction") {
      // Adicionar transação à seleção
      if (!selectedTransactions.includes(draggedItem.id)) {
        setSelectedTransactions([...selectedTransactions, draggedItem.id]);
      }
      if (!selectedInvoices.includes(targetInvoiceId)) {
        setSelectedInvoices([...selectedInvoices, targetInvoiceId]);
      }
    }
    setDraggedItem(null);
  };

  const handleDropOnTransaction = (targetTransactionId: number) => {
    if (!draggedItem) return;

    if (draggedItem.type === "invoice") {
      // Adicionar fatura à seleção
      if (!selectedInvoices.includes(draggedItem.id)) {
        setSelectedInvoices([...selectedInvoices, draggedItem.id]);
      }
      if (!selectedTransactions.includes(targetTransactionId)) {
        setSelectedTransactions([...selectedTransactions, targetTransactionId]);
      }
    }
    setDraggedItem(null);
  };

  // Criar match
  const handleCreateMatch = async () => {
    if (selectedInvoices.length === 0 || selectedTransactions.length === 0) {
      setError("Selecione pelo menos uma fatura e uma transação");
      return;
    }

    const totalInvoices = selectedInvoices.reduce((sum, id) => {
      const invoice = invoices.find((i) => i.id === id);
      return sum + (invoice?.valor || 0);
    }, 0);

    const totalTransactions = selectedTransactions.reduce((sum, id) => {
      const transaction = transactions.find((t) => t.id === id);
      return sum + Math.abs(transaction?.valor || 0);
    }, 0);

    const isComplete = Math.abs(totalInvoices - totalTransactions) < 0.01;

    try {
      setLoading(true);

      // Criar matches individuais para cada combinação
      for (const invoiceId of selectedInvoices) {
        for (const transactionId of selectedTransactions) {
          const match = await matchingService.createMatch({
            invoice_id: invoiceId,
            transaction_id: transactionId,
            amount_matched: totalInvoices / selectedInvoices.length,
            matched_by: "user",
          });

          // Auto-aprovar se for match completo
          if (isComplete && match.id) {
            await matchingService.approveMatch(match.id, "user");
          }
        }
      }

      // Atualizar estados locais - não remover, apenas marcar
      if (isComplete) {
        // Match completo - marcar como matched (não remover)
        setInvoices((prev) =>
          prev.map((i) =>
            selectedInvoices.includes(i.id)
              ? { ...i, matched: true, matchedAmount: totalTransactions }
              : i
          )
        );
        setTransactions((prev) =>
          prev.map((t) =>
            selectedTransactions.includes(t.id)
              ? { ...t, matched: true, matchedAmount: totalInvoices }
              : t
          )
        );
      } else {
        // Match parcial - marcar como parcialmente matched
        setInvoices((prev) =>
          prev.map((i) =>
            selectedInvoices.includes(i.id)
              ? { ...i, partiallyMatched: true, matchedAmount: totalTransactions }
              : i
          )
        );
        setTransactions((prev) =>
          prev.map((t) =>
            selectedTransactions.includes(t.id)
              ? { ...t, partiallyMatched: true, matchedAmount: totalInvoices }
              : t
          )
        );
      }

      setSelectedInvoices([]);
      setSelectedTransactions([]);
      setError(null);
    } catch (err) {
      setError("Erro ao criar match");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Limpar seleção
  const handleClearSelection = () => {
    setSelectedInvoices([]);
    setSelectedTransactions([]);
    setExistingMatches([]);
  };

  // Carregar matches existentes dos itens selecionados
  const loadExistingMatches = useCallback(async () => {
    if (selectedInvoices.length === 0 && selectedTransactions.length === 0) {
      setExistingMatches([]);
      return;
    }

    try {
      setLoadingMatches(true);
      const matches: any[] = [];

      // Carregar matches das faturas selecionadas
      for (const invoiceId of selectedInvoices) {
        const response = await fetch(`/api/matches/invoice/${invoiceId}`);
        const data = await response.json();
        if (data.success && data.matches) {
          matches.push(...data.matches);
        }
      }

      // Carregar matches das transações selecionadas
      for (const transactionId of selectedTransactions) {
        const response = await fetch(`/api/matches/transaction/${transactionId}`);
        const data = await response.json();
        if (data.success && data.matches) {
          matches.push(...data.matches);
        }
      }

      // Remover duplicados
      const uniqueMatches = Array.from(
        new Map(matches.map(m => [m.id, m])).values()
      );

      setExistingMatches(uniqueMatches);
    } catch (err) {
      console.error("Erro ao carregar matches existentes:", err);
      setExistingMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  }, [selectedInvoices, selectedTransactions]);

  // Carregar matches existentes quando seleção muda
  useEffect(() => {
    loadExistingMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoices, selectedTransactions]);

  // Deletar match
  const handleDeleteMatch = async (matchId: number) => {
    try {
      setLoading(true);
      await matchingService.deleteMatch(matchId);

      // Recarregar dados
      await Promise.all([
        loadTransactions(1),
        loadInvoices(1),
      ]);

      // Recarregar matches existentes
      await loadExistingMatches();

      setError(null);
    } catch (err) {
      setError("Erro ao deletar match");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Calcular totais da seleção
  const selectedInvoicesTotal = selectedInvoices.reduce((sum, id) => {
    const invoice = invoices.find((i) => i.id === id);
    return sum + (invoice?.valor || 0);
  }, 0);

  const selectedTransactionsTotal = selectedTransactions.reduce((sum, id) => {
    const transaction = transactions.find((t) => t.id === id);
    return sum + Math.abs(transaction?.valor || 0);
  }, 0);

  const isMatchComplete =
    Math.abs(selectedInvoicesTotal - selectedTransactionsTotal) < 0.01;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, height: "calc(100vh - 180px)" }}>
      {/* Header */}
      <Box>
        <Typography variant="h4" gutterBottom>
          Matching Avançado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Arraste faturas e transações para criar matches. Suporta matches parciais.
        </Typography>
      </Box>

      {/* Selection Info */}
      {(selectedInvoices.length > 0 || selectedTransactions.length > 0) && (
        <Paper sx={{ p: 2, bgcolor: "primary.light", color: "white" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1">
                Faturas selecionadas: {selectedInvoices.length} (€
                {selectedInvoicesTotal.toFixed(2)})
              </Typography>
              <Typography variant="subtitle1">
                Transações selecionadas: {selectedTransactions.length} (€
                {selectedTransactionsTotal.toFixed(2)})
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {isMatchComplete ? (
                  <>
                    <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Match Completo
                  </>
                ) : (
                  <>
                    Match Parcial - Diferença: €
                    {Math.abs(selectedInvoicesTotal - selectedTransactionsTotal).toFixed(2)}
                  </>
                )}
              </Typography>

              {/* Mostrar matches existentes */}
              {existingMatches.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "rgba(255,255,255,0.3)" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Matches Existentes: {existingMatches.length}
                  </Typography>
                  {existingMatches.map((match) => (
                    <Box
                      key={match.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                        fontSize: "0.875rem",
                      }}
                    >
                      <Typography variant="caption">
                        Match #{match.id} - €{parseFloat(match.amount_matched).toFixed(2)}
                        {match.status === "approved" && " (Aprovado)"}
                        {match.status === "pending" && " (Pendente)"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMatch(match.id)}
                        sx={{ color: "white", p: 0.5 }}
                        title="Deletar match"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleCreateMatch}
                disabled={loading || loadingMatches}
              >
                Criar Match
              </Button>
              <Button variant="outlined" color="inherit" onClick={handleClearSelection}>
                Limpar
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {/* Dual Lists */}
      <Box sx={{ display: "flex", gap: 2, flexGrow: 1, overflow: "hidden" }}>
        {/* Transações Bancárias */}
        <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6">Transações Bancárias</Typography>
            <Typography variant="caption" color="text.secondary">
              {transactions.length} transações
            </Typography>
          </Box>
          <Box
            ref={transactionsRef}
            onScroll={handleTransactionsScroll}
            sx={{
              flexGrow: 1,
              overflow: "auto",
              p: 1,
            }}
          >
            {transactions.map((transaction) => (
              <Box
                key={transaction.id}
                draggable
                onDragStart={() => handleDragStart("transaction", transaction.id)}
                onDragOver={handleDragOver}
                onDrop={() => handleDropOnTransaction(transaction.id)}
                onClick={() => {
                  if (selectedTransactions.includes(transaction.id)) {
                    setSelectedTransactions(
                      selectedTransactions.filter((id) => id !== transaction.id)
                    );
                  } else {
                    setSelectedTransactions([...selectedTransactions, transaction.id]);
                  }
                }}
                sx={{
                  p: 1,
                  mb: 0.5,
                  border: 1,
                  borderColor: selectedTransactions.includes(transaction.id)
                    ? "primary.main"
                    : "divider",
                  borderRadius: 1,
                  cursor: "grab",
                  bgcolor: transaction.matched
                    ? "action.disabledBackground"
                    : transaction.partiallyMatched
                    ? "action.hover"
                    : "background.paper",
                  opacity: transaction.matched ? 0.5 : 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "&:active": {
                    cursor: "grabbing",
                  },
                }}
              >
                {/* Linha 1: Data e Valor (destacados) */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(transaction.data_movimento).toLocaleDateString("pt-PT")}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color={transaction.valor < 0 ? "error" : "success.main"}
                  >
                    €{Math.abs(transaction.valor).toFixed(2)}
                  </Typography>
                </Box>
                {/* Linha 2: Banco/Conta, descrição e chips de status */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                    {transaction.banco && transaction.conta
                      ? `${transaction.banco} (${transaction.conta}) - ${transaction.descricao}`
                      : transaction.descricao}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {transaction.partiallyMatched && (
                      <Chip label="Parcial" size="small" color="warning" />
                    )}
                    {transaction.matched && (
                      <Chip label="Matched" size="small" color="success" />
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            {hasMoreTransactions && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Paper>

        {/* Faturas */}
        <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6">Faturas</Typography>
            <Typography variant="caption" color="text.secondary">
              {invoices.length} faturas
            </Typography>
          </Box>
          <Box
            ref={invoicesRef}
            onScroll={handleInvoicesScroll}
            sx={{
              flexGrow: 1,
              overflow: "auto",
              p: 1,
            }}
          >
            {invoices.map((invoice) => (
              <Box
                key={invoice.id}
                draggable
                onDragStart={() => handleDragStart("invoice", invoice.id)}
                onDragOver={handleDragOver}
                onDrop={() => handleDropOnInvoice(invoice.id)}
                onClick={() => {
                  if (selectedInvoices.includes(invoice.id)) {
                    setSelectedInvoices(
                      selectedInvoices.filter((id) => id !== invoice.id)
                    );
                  } else {
                    setSelectedInvoices([...selectedInvoices, invoice.id]);
                  }
                }}
                sx={{
                  p: 1,
                  mb: 0.5,
                  border: 1,
                  borderColor: selectedInvoices.includes(invoice.id)
                    ? "primary.main"
                    : "divider",
                  borderRadius: 1,
                  cursor: "grab",
                  bgcolor: invoice.matched
                    ? "action.disabledBackground"
                    : invoice.partiallyMatched
                    ? "action.hover"
                    : "background.paper",
                  opacity: invoice.matched ? 0.5 : 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  "&:active": {
                    cursor: "grabbing",
                  },
                }}
              >
                {/* Linha 1: Data e Valor (destacados) */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(invoice.data_emissao).toLocaleDateString("pt-PT")}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    €{invoice.valor.toFixed(2)}
                  </Typography>
                </Box>
                {/* Linha 2: Nome empresa, número fatura e chips */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                    {invoice.emitente_nome_curto || invoice.emitente_nome || `NIF ${invoice.nif_emitente}`} - {invoice.numero_fatura}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {invoice.partiallyMatched && (
                      <Chip label="Parcial" size="small" color="warning" />
                    )}
                    {invoice.matched && (
                      <Chip label="Matched" size="small" color="success" />
                    )}
                    <Chip label={invoice.categoria} size="small" />
                  </Box>
                </Box>
              </Box>
            ))}
            {hasMoreInvoices && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AdvancedMatchingPage;
